/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  Progress,
  TimePicker,
  Typography,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TContext, TPrivilege, TResponseModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TCrateAssign } from "../../../types";
import {
  GetScannedCrateList,
  UpdateCrateMapping,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import "../../../index.css";
import { requiredFieldValidator } from "../../../utils/input.validator";
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
} from "@ant-design/icons";

type DrawerProps = {
  scannedIds: number[];
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function CrateAssignForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);
  const [crateOptions, setCrateOptions] = useState<any[]>([]);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  // Hook to handle on load operations
  useEffect(() => {
    const fetchData = async () => {
      await fetchCrateOptions();
    };

    fetchData(); // Call the fetch function
  }, []);

  // Handle on submit event
  const onFinish = async (values: { crateId: number }) => {
    try {
      const payload: TCrateAssign = {
        id: values.crateId,
        scannedProductList: props.scannedIds.map((id) => ({
          id: id as number,
        })),
      };
      setIsLoading(true);

      const response = await post(UpdateCrateMapping, { data: payload });
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchCrateOptions = async () => {
    try {
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetScannedCrateList,
        requestPayload,
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedOptions = data.map((crate: any) => ({
          value: crate.id,
          label: crate.serialNumber,
          data: crate,
        }));
        setCrateOptions(formattedOptions);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <Row
            // gutter={16}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                Assign Crate
              </h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Typography.Text>
                Total {props?.scannedIds?.length} Scanned Blower Selected
              </Typography.Text>
            </Col>
            <Col span={24} style={{ marginTop: "15px" }}>
              <Form.Item
                name="crateId"
                label="Assign Crate"
                rules={[
                  {
                    required: true,
                    message: "Please select a crate!",
                  },
                  {
                    validator: async (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const selectedCrate = crateOptions.find(
                        (c) => c.value === value,
                      );
                      if (selectedCrate) {
                        const availableSlots =
                          selectedCrate.data.crateCapacity -
                          selectedCrate.data.blowerCount;
                        if (props.scannedIds.length > availableSlots) {
                          return Promise.reject(
                            new Error(
                              `Not enough space. Available: ${availableSlots}, Selected: ${props.scannedIds.length}`,
                            ),
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  size="large"
                  disabled={isLoading}
                  placeholder="Select crate"
                  options={crateOptions}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  optionRender={(option) => (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={{ flex: 1 }}>
                        <div>{option.data.label}</div>
                        <Progress
                          percent={
                            (option.data.data.blowerCount /
                              option.data.data.crateCapacity) *
                            100
                          }
                          showInfo={false}
                        />
                      </div>
                      <div style={{ marginLeft: "10px" }}>
                        {`${option.data.data.blowerCount}/${option.data.data.crateCapacity}`}
                      </div>
                    </div>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              size="large"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              loading={isLoading}
              className="btnDimensions"
            >
              Save
            </Button>
            {/* <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button> */}
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
